/**
 * PeerConnection — Wraps a single RTCPeerConnection with state machine, events, and lifecycle.
 *
 * Emits events on the provided EventBus using getRobotEvent(peerId, ...) pattern.
 * No React dependencies — pure JS class.
 */

import { getDefaultPeerConnectionConfig } from "shared/config/peer_conn_config";
import { ROBOT_EVENTS, WEBRTC_EVENTS, getRobotEvent, nomalizeRobotTopicName } from "shared/services/eventBus/eventTypes";
import {
  PEER_STATES,
  PEER_INTERNAL_EVENTS,
  isValidTransition,
  DEFAULT_DATA_CHANNELS_CONFIG,
  RECONNECT_DEFAULTS,
} from "./peerStates";
import { parseStatsReport, gradeSample, computeVideoHealth, STALL_SAMPLES } from "./peerStatsParser";
import logger from "shared/utils/logging";
import * as peerMedia from "features/robot-teleop/utils/peerMedia";

const DEFAULT_STATS_INTERVAL_MS = 1000;
const STATS_HISTORY_SIZE = 10; // samples kept inside the peer (enough for stall detection)
// Chrome can sit in connectionState 'disconnected' for a long time before it
// reports 'failed' (and never does for some consent-freshness failures). After
// this grace period a disconnected transport is treated like a failure so the
// normal retry/backoff path brings the media back.
const DISCONNECTED_GRACE_MS = 5000;
// An offer that gets no answer, or an answer whose ICE never connects, must not
// leave the peer in CONNECTING forever: both feed the normal retry/backoff path.
// aiortc answers in ~0.5-3 s (it gathers all candidates before answering).
const ANSWER_TIMEOUT_MS = 12000;
const ICE_CONNECT_TIMEOUT_MS = 20000;

export class PeerConnection {
  /**
   * @param {string} peerId
   * @param {Object} config
   * @param {Object} config.peerInfo - the peer as registered with conn-manager (one of a robot's "robot clients"):
   *   { id, robot_id, setting: { cameras, microphones, speakers, is_control_channel }, status, ... }
   * @param {Object} config.signalingBridge - { sendOffer, sendIceCandidate, sendMediaConfig, sendEvent }
   * @param {Object} config.eventBus - EventBus instance with publish/subscribe
   * @param {Object} [config.iceConfig] - RTCPeerConnection ICE config
   * @param {Object} [config.dataChannelsConfig] - Data channel definitions
   * @param {boolean} [config.autoConnect=false]
   * @param {boolean} [config.trickleICE=true]
   * @param {number} [config.maxRetries]
   * @param {number} [config.baseRetryDelay]
   * @param {boolean} [config.publishTopicMessages=true]
   * @param {boolean} [config.sendLocalAudio] - Add the local audio track to the offer (default: robot has a speaker)
   * @param {boolean} [config.microphoneEnabled=false] - Send the browser microphone to this peer (setMicrophoneEnabled)
   * @param {boolean} [config.remoteAudioEnabled=true] - Receive this peer's microphone (setRemoteAudioEnabled)
   * @param {Function} [config.getMicrophoneTrack] - () => live microphone MediaStreamTrack or null (SharedMediaManager)
   * @param {boolean} [config.sendLocalVideo=false] - Add the local video track to the offer
   */
  constructor(peerId, config) {
    this.peerId = peerId;
    this.peerInfo = config.peerInfo;
    this.signalingBridge = config.signalingBridge;
    this.eventBus = config.eventBus;
    this.iceConfig = config.iceConfig || getDefaultPeerConnectionConfig();
    this.dataChannelsConfig = config.dataChannelsConfig || DEFAULT_DATA_CHANNELS_CONFIG;
    this._autoConnect = config.autoConnect ?? false;
    this.trickleICE = config.trickleICE ?? true;
    this.maxRetries = config.maxRetries ?? RECONNECT_DEFAULTS.maxRetries;
    this.baseRetryDelay = config.baseRetryDelay ?? RECONNECT_DEFAULTS.baseDelay;
    this.publishTopicMessages = config.publishTopicMessages ?? true;
    this.sendLocalAudio = config.sendLocalAudio; // undefined → decided per connect from hasSpeaker
    this.sendLocalVideo = config.sendLocalVideo ?? false;
    // Per-peer audio settings, changed at runtime from the tile controls.
    this._microphoneEnabled = config.microphoneEnabled ?? false; // send the browser microphone to this peer
    this._remoteAudioEnabled = config.remoteAudioEnabled ?? true; // receive this peer's microphone
    this._getMicrophoneTrack = typeof config.getMicrophoneTrack === "function" ? config.getMicrophoneTrack : () => null;
    this._userClosed = false; // disconnected by the user: auto-reconnect leaves it alone
    this._localAudioSender = null;        // sender of the audio m-line of the current connection
    this._localAudioFallbackTrack = null; // the shared (silent) track that sender started with

    // State
    this._state = PEER_STATES.IDLE;
    this._iceState = null;
    this._connectionState = null;
    this._retryCount = 0;
    this._wasConnected = false; // true if peer was ever connected (for reconnection decisions)
    this._destroyed = false;

    // WebRTC internals
    this._pc = null;
    this._dataChannels = {};
    this._remoteTracks = new Set();
    this._pendingRemoteCandidates = [];
    this._remoteDescriptionSet = false;
    this._connectSeq = 0; // bumped on every connect/close so late async results of older attempts are ignored
    this._reconnectTimer = null;
    this._disconnectedTimer = null;
    this._answerTimer = null;
    this._iceTimer = null;
    this._attemptStartedAt = 0;

    // Stats / media health. Stats collection is reference counted (several UI
    // components may watch the same peer) and survives reconnects: the timer is
    // (re)started for every new RTCPeerConnection while _statsUsers > 0.
    this._statsUsers = 0;
    this._statsIntervalMs = DEFAULT_STATS_INTERVAL_MS;
    this._statsTimer = null;
    this._statsSamples = [];       // last STATS_HISTORY_SIZE normalized samples of the current connection
    this._videoHealth = "unknown"; // 'ok' | 'stalled' | 'no-track' | 'unknown'
    this._remoteTrackCleanups = []; // listeners attached to remote tracks (mute/unmute/ended)
  }

  // ==================== Properties ====================

  get autoConnect() { return this._autoConnect; }
  set autoConnect(value) { this._autoConnect = !!value; }

  // `setting.*` is what the peer registers (ketirtc get_media_configs); a
  // DummyCamera is not a camera, and the legacy flat `has_*` flags are honoured
  // too so that a peer advertising a camera only through `has_camera` still
  // gets a video m-line in the offer (features/robot-teleop/utils/peerMedia.js).
  get cameras() { return peerMedia.getPeerCameras(this.peerInfo); }
  get microphones() { return peerMedia.getPeerMicrophones(this.peerInfo); }
  get speakers() { return peerMedia.getPeerSpeakers(this.peerInfo); }
  get hasCamera() { return peerMedia.peerHasCamera(this.peerInfo); }
  get hasMicrophone() { return peerMedia.peerHasMicrophones(this.peerInfo); }
  get hasSpeaker() { return peerMedia.peerHasSpeaker(this.peerInfo); }
  get isControlChannel() { return !!this.peerInfo?.setting?.is_control_channel; }
  get robotId() { return this.peerInfo?.robot_id; }
  get microphoneEnabled() { return this._microphoneEnabled; }
  get remoteAudioEnabled() { return this._remoteAudioEnabled; }
  /** true after the user disconnected this peer; ConnectionManager's automatic paths skip it */
  get userClosed() { return this._userClosed; }
  set userClosed(value) { this._userClosed = !!value; }

  get state() { return this._state; }
  get iceState() { return this._iceState; }
  get connectionState() { return this._connectionState; }
  get wasConnected() { return this._wasConnected; }
  get retryCount() { return this._retryCount; }
  get isDestroyed() { return this._destroyed; }

  /** Remote track events received on the current RTCPeerConnection (RTCTrackEvent[]). */
  get remoteTrackEvents() { return Array.from(this._remoteTracks); }

  /** First live remote video track of the current connection, or null. */
  get remoteVideoTrack() {
    for (const evt of this._remoteTracks) {
      const track = evt?.track;
      if (track && track.kind === "video" && track.readyState === "live") return track;
    }
    return null;
  }

  /** 'ok' | 'stalled' | 'no-track' | 'unknown' — see peerStatsParser.computeVideoHealth. */
  get videoHealth() { return this._videoHealth; }

  /** Most recent normalized stats sample of the current connection (or null). */
  get lastStatsSample() { return this._statsSamples.length ? this._statsSamples[this._statsSamples.length - 1] : null; }

  /** Recent normalized stats samples of the current connection (oldest first). */
  get statsHistory() { return this._statsSamples.slice(); }

  get statsEnabled() { return this._statsUsers > 0; }

  // ==================== State Machine ====================

  /**
   * @returns {boolean} true if the peer is now in `newState` (including no-op), false if the transition was rejected
   */
  _setState(newState) {
    const oldState = this._state;
    if (oldState === newState) return true;

    if (!isValidTransition(oldState, newState)) {
      logger.warn(`[PeerConnection:${this.peerId}] Invalid transition: ${oldState} → ${newState}`);
      return false;
    }

    this._state = newState;
    logger.log(`[PeerConnection:${this.peerId}] ${oldState} → ${newState}`);
    this.eventBus.publish(
      getRobotEvent(this.peerId, ROBOT_EVENTS.CONNECTION_STATE),
      {
        peerId: this.peerId,
        state: newState,
        previousState: oldState,
        iceState: this._iceState,
        connectionState: this._connectionState,
        retryCount: this._retryCount,
      }
    );
    return true;
  }

  // ==================== Lifecycle ====================

  /**
   * Create RTCPeerConnection, add local tracks, create offer, and send via signaling.
   * Can be called from IDLE, FAILED, CLOSED or RECONNECTING. CONNECTING/CONNECTED is a no-op.
   * @param {MediaStream|null} localStream - Shared local media stream (from SharedMediaManager)
   */
  async connect(localStream) {
    if (this._destroyed) return;
    if (this._state === PEER_STATES.CONNECTING || this._state === PEER_STATES.CONNECTED) {
      logger.log(`[PeerConnection:${this.peerId}] Already ${this._state}, skipping connect`);
      return;
    }

    // A fresh connect starts a new retry budget; a connect issued by the retry
    // path (state RECONNECTING) keeps counting towards maxRetries.
    if (this._state !== PEER_STATES.RECONNECTING) {
      this._retryCount = 0;
    }
    this._clearReconnectTimer();
    this._setState(PEER_STATES.CONNECTING);
    const seq = ++this._connectSeq;
    this._attemptStartedAt = Date.now();

    let pc = null;
    try {
      pc = this._initPeerConnection();
      this._addLocalTracks(localStream);

      const offerOptions = {
        offerToReceiveAudio: this.hasMicrophone || this.hasSpeaker,
        offerToReceiveVideo: this.hasCamera,
      };

      const offer = await pc.createOffer(offerOptions);
      if (this._isStaleAttempt(seq, pc)) return;
      await pc.setLocalDescription(offer);
      if (this._isStaleAttempt(seq, pc)) return;

      // Wait for ICE gathering if not using trickle ICE
      if (!this.trickleICE) {
        await this._waitForIceGathering(pc);
        if (this._isStaleAttempt(seq, pc)) return;
      }

      const localDesc = pc.localDescription;
      logger.log(`[PeerConnection:${this.peerId}] Sending offer`);

      // `attempt` is echoed by the robot in its answer so that the answer of a
      // superseded offer is never applied to a newer connection.
      await this.signalingBridge.sendOffer(this.peerId, {
        sdp: localDesc.sdp,
        type: localDesc.type,
        attempt: seq,
      });
      if (this._isStaleAttempt(seq, pc)) return;
      this._armAnswerTimer(seq, pc);
    } catch (err) {
      if (this._isStaleAttempt(seq, pc)) return;
      if (this._remoteDescriptionSet || this._state === PEER_STATES.CONNECTED) {
        // The robot answered before the signaling request settled: a late or
        // lost command ack must not tear down a working connection.
        logger.warn(`[PeerConnection:${this.peerId}] Offer request failed after the answer arrived, ignoring:`, err);
        return;
      }
      logger.error(`[PeerConnection:${this.peerId}] Connect error:`, err);
      // The offer never reached the robot — release this attempt's connection and
      // treat it like a failed connection: retry with backoff, FAILED after maxRetries.
      this._closePeerConnection();
      this._handleConnectionFailed(err);
    }
  }

  /**
   * Close existing connection and reconnect (fresh retry budget).
   * @param {MediaStream|null} localStream
   */
  async reconnect(localStream, { force = false } = {}) {
    if (this._destroyed) return;
    // Never put a second offer on the wire while the previous one is still
    // being answered (the robot would close the connection it is negotiating
    // and the first answer could land on the new connection). The answer
    // timeout retries on its own if that offer was lost.
    const attemptAge = Date.now() - this._attemptStartedAt;
    if (!force && this._state === PEER_STATES.CONNECTING && attemptAge < ANSWER_TIMEOUT_MS) {
      logger.log(`[PeerConnection:${this.peerId}] Offer in flight (${attemptAge}ms), reconnect skipped`);
      return;
    }
    this._clearReconnectTimer();
    // No disconnect2robot here: the robot replaces its connection when the new
    // offer arrives, and a disconnect racing that offer would close the new one.
    this._closePeerConnection();
    this._retryCount = 0;
    this._setState(PEER_STATES.RECONNECTING);
    const seq = ++this._connectSeq;
    // Small delay before reconnect
    await new Promise(resolve => setTimeout(resolve, 500));
    if (this._destroyed || seq !== this._connectSeq) return;
    await this.connect(localStream);
  }

  /**
   * Fully close and teardown the peer connection.
   */
  close() {
    this._clearReconnectTimer();
    this._connectSeq++;
    const hadConnection = this._pc !== null;
    this._closePeerConnection();
    this._setState(PEER_STATES.CLOSED);
    if (hadConnection) {
      this._notifyConnectionState("closed");
      // Tell the robot right away (otherwise it keeps our peer, camera track
      // included, until its ICE consent checks expire ~30 s later).
      this._sendDisconnect();
    }
  }

  /**
   * Full teardown — called by ConnectionManager.destroy().
   */
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    this.close();
  }

  // ==================== Signaling Handlers ====================

  /**
   * Handle SDP answer from robot.
   * @param {Object} sdp - { type: "answer", sdp: "..." } (extra fields such as `from` are ignored)
   */
  handleRemoteSDP(sdp) {
    const pc = this._pc;
    if (!pc) {
      logger.warn(`[PeerConnection:${this.peerId}] Cannot set remote description: PC is null`);
      return;
    }
    const description = { type: sdp?.type || "answer", sdp: sdp?.sdp };
    if (!description.sdp) {
      logger.warn(`[PeerConnection:${this.peerId}] Ignoring remote description without sdp:`, sdp);
      return;
    }
    if (sdp?.attempt != null && String(sdp.attempt) !== String(this._connectSeq)) {
      // Answer of an offer this peer already abandoned (re-offer, retry): its
      // ICE credentials and DTLS fingerprint belong to a connection the robot
      // has closed, applying it would wedge the current attempt.
      logger.warn(`[PeerConnection:${this.peerId}] Ignoring answer of superseded attempt ${sdp.attempt} (current ${this._connectSeq})`);
      return;
    }
    if (description.type === "answer" && pc.signalingState !== "have-local-offer") {
      // Stale answer (e.g. for an offer of a previous attempt) — applying it would
      // throw "wrong state" or, worse, bind this connection to the wrong session.
      logger.warn(`[PeerConnection:${this.peerId}] Ignoring answer in signaling state '${pc.signalingState}'`);
      return;
    }

    pc.setRemoteDescription(description).then(
      () => {
        if (this._pc !== pc) return;
        logger.log(`[PeerConnection:${this.peerId}] Remote description set`);
        this._remoteDescriptionSet = true;
        this._clearAnswerTimer();
        this._armIceTimer(pc);
        this._flushPendingRemoteCandidates(pc);
      },
      (err) => {
        logger.error(`[PeerConnection:${this.peerId}] Failed to set remote description:`, err);
      }
    );
  }

  /**
   * Handle remote ICE candidate. Queued until the remote description is set.
   * @param {Object} candidate - RTCIceCandidate(Init)
   */
  handleRemoteIceCandidate(candidate) {
    const pc = this._pc;
    if (!pc) {
      logger.warn(`[PeerConnection:${this.peerId}] Cannot add ICE candidate: PC is null`);
      return;
    }
    if (!this._remoteDescriptionSet) {
      this._pendingRemoteCandidates.push(candidate);
      return;
    }
    this._addRemoteIceCandidate(pc, candidate);
  }

  _addRemoteIceCandidate(pc, candidate) {
    pc.addIceCandidate(candidate).then(
      () => logger.debug(`[PeerConnection:${this.peerId}] ICE candidate added`),
      (err) => logger.error(`[PeerConnection:${this.peerId}] Failed to add ICE candidate:`, err)
    );
  }

  _flushPendingRemoteCandidates(pc) {
    const pending = this._pendingRemoteCandidates;
    this._pendingRemoteCandidates = [];
    pending.forEach((candidate) => this._addRemoteIceCandidate(pc, candidate));
  }

  // ==================== Media Control ====================

  /**
   * Replace local tracks on existing peer connection (without renegotiation).
   * @param {MediaStream} stream
   */
  setLocalStream(stream) {
    if (!this._pc || !stream) return;
    const senders = this._pc.getSenders();
    senders.forEach((sender) => {
      const track = sender.track;
      if (track) {
        const newTrack = stream.getTracks().find((t) => t.kind === track.kind);
        if (newTrack && newTrack !== track) {
          track.stop();
          sender.replaceTrack(newTrack);
        }
      }
    });
  }

  /**
   * Mute/unmute local audio track on this peer connection.
   */
  muteLocalAudio(muted) {
    if (!this._pc) return;
    this._pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === "audio") {
        sender.track.enabled = !muted;
      }
    });
  }

  /**
   * Mute/unmute local video track on this peer connection.
   */
  muteLocalVideo(muted) {
    if (!this._pc) return;
    this._pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === "video") {
        sender.track.enabled = !muted;
      }
    });
  }

  /**
   * Send media config signal to remote robot (toggle remote video/audio).
   * @param {Object} config - { video: { enabled }, audio: { enabled }, ... }
   */
  async sendMediaConfig(config) {
    return this.signalingBridge.sendMediaConfig(this.peerId, config);
  }

  // ==================== Per-peer audio ====================

  /**
   * Send (or stop sending) the browser microphone to THIS peer. Other peers
   * share the same microphone track but keep their own setting: the track is
   * swapped on this peer's audio sender (replaceTrack, no renegotiation), and
   * the shared silent track is put back when the microphone is switched off.
   * Without a live connection the setting is remembered and applied on connect.
   * @param {boolean} enabled
   * @param {MediaStreamTrack|null} [microphoneTrack] - the live microphone; defaults to config.getMicrophoneTrack()
   */
  setMicrophoneEnabled(enabled, microphoneTrack = null) {
    this._microphoneEnabled = !!enabled;
    if (this._microphoneEnabled) {
      const mic = microphoneTrack || this._getMicrophoneTrack();
      if (mic) this._replaceLocalAudioTrack(mic);
    } else {
      const fallback = this._localAudioFallbackTrack;
      this._replaceLocalAudioTrack(fallback && fallback.readyState === "live" ? fallback : null);
    }
    this._publishMediaConfig();
  }

  /**
   * Receive (or mute) this peer's microphone. The UI mutes playback at once;
   * the robot is asked to stop/resume sending through a media_config signal,
   * repeated on every (re)connect because a new offer resets the robot's side.
   * @param {boolean} enabled
   */
  setRemoteAudioEnabled(enabled) {
    this._remoteAudioEnabled = !!enabled;
    if (this._state === PEER_STATES.CONNECTED) this._sendRemoteAudioConfig();
    this._publishMediaConfig();
  }

  _replaceLocalAudioTrack(track) {
    const sender = this._localAudioSender;
    if (!sender || typeof sender.replaceTrack !== "function") return;
    Promise.resolve()
      .then(() => sender.replaceTrack(track))
      .catch((err) => logger.warn(`[PeerConnection:${this.peerId}] replaceTrack(${track ? track.kind : "null"}) failed:`, err));
  }

  _sendRemoteAudioConfig() {
    // A plain `signal` reaches the robot's signal handler, which applies audio
    // config; a request (sendMediaConfig) goes through its command path, which
    // only knows video config.
    const send = this.signalingBridge.sendMediaSignal || this.signalingBridge.sendMediaConfig;
    if (typeof send !== "function") return;
    const config = { audio: { enabled: this._remoteAudioEnabled } };
    Promise.resolve()
      .then(() => send(this.peerId, config))
      .catch((err) => logger.warn(`[PeerConnection:${this.peerId}] media config not delivered:`, err));
  }

  _publishMediaConfig() {
    this.eventBus.publish(getRobotEvent(this.peerId, PEER_INTERNAL_EVENTS.MEDIA_CONFIG_CHANGED), {
      peerId: this.peerId,
      microphoneEnabled: this._microphoneEnabled,
      remoteAudioEnabled: this._remoteAudioEnabled,
    });
  }

  // ==================== Data Channels ====================

  /**
   * Send data on a named data channel.
   * @param {string} label - Channel name (e.g., "chat")
   * @param {string|ArrayBuffer} data
   * @returns {boolean} true if the data was handed to the channel
   */
  channelSend(label, data) {
    const dc = this._dataChannels[label];
    if (!dc) {
      logger.warn(`[PeerConnection:${this.peerId}] No data channel '${label}'`);
      return false;
    }
    if (dc.readyState !== "open") {
      logger.warn(`[PeerConnection:${this.peerId}] Data channel '${label}' not open (${dc.readyState})`);
      return false;
    }
    try {
      dc.send(data);
      return true;
    } catch (err) {
      logger.error(`[PeerConnection:${this.peerId}] Data channel '${label}' send failed:`, err);
      return false;
    }
  }

  /**
   * Get a data channel by label.
   * @param {string} label
   * @returns {RTCDataChannel|null}
   */
  getChannel(label) {
    return this._dataChannels[label] || null;
  }

  /**
   * @param {string} label
   * @returns {boolean} true if the channel exists and is open
   */
  isChannelOpen(label) {
    return this._dataChannels[label]?.readyState === "open";
  }

  // ==================== Stats ====================

  /**
   * Start collecting stats (reference counted). Every call must be paired with
   * a disableStats() call. The collection is bound to the current connection and
   * automatically restarts after a reconnect while at least one user remains.
   * @param {number} [intervalMs]
   */
  enableStats(intervalMs = DEFAULT_STATS_INTERVAL_MS) {
    this._statsUsers++;
    if (intervalMs && intervalMs !== this._statsIntervalMs) {
      this._statsIntervalMs = intervalMs;
      this._stopStatsTimer();
    }
    this._startStatsTimer();
  }

  /** Release one stats user; the timer stops when nobody is listening anymore. */
  disableStats() {
    this._statsUsers = Math.max(0, this._statsUsers - 1);
    if (this._statsUsers === 0) this._stopStatsTimer();
  }

  _startStatsTimer() {
    if (this._statsTimer || this._statsUsers <= 0 || !this._pc || this._destroyed) return;
    this._statsTimer = setInterval(() => this._collectStats(), this._statsIntervalMs);
  }

  _stopStatsTimer() {
    if (this._statsTimer) {
      clearInterval(this._statsTimer);
      this._statsTimer = null;
    }
  }

  _resetStats() {
    this._statsSamples = [];
    this._setVideoHealth("unknown");
  }

  /**
   * Recompute the media health from the connection, the remote video track and
   * the recent samples; publishes MEDIA_STATE_CHANGED when it changes.
   */
  _updateVideoHealth(extra = {}) {
    const track = this.remoteVideoTrack;
    const health = computeVideoHealth({
      connected: this._state === PEER_STATES.CONNECTED && this._connectionState === "connected",
      expectsVideo: this.hasCamera,
      hasVideoTrack: !!track,
      trackMuted: !!track && track.muted === true,
      samples: this._statsSamples,
      previous: this._videoHealth,
    });
    if (health === "stalled" && this._videoHealth !== "stalled"
        && typeof document !== "undefined" && document.hidden) {
      // Background tab: the browser may legitimately stop decoding; judge again
      // once the page is visible.
      return this._videoHealth;
    }
    this._setVideoHealth(health, extra);
    return health;
  }

  _setVideoHealth(health, extra = {}) {
    if (health === this._videoHealth) return;
    const previous = this._videoHealth;
    this._videoHealth = health;
    logger.log(`[PeerConnection:${this.peerId}] Video health: ${previous} → ${health}`);
    this.eventBus.publish(
      getRobotEvent(this.peerId, ROBOT_EVENTS.MEDIA_STATE_CHANGED),
      { peerId: this.peerId, health, previousHealth: previous, ...extra }
    );
  }

  _watchRemoteTrack(pc, track) {
    if (!track || typeof track.addEventListener !== "function") return;
    const onChange = (evt) => {
      if (this._pc !== pc) return;
      logger.log(`[PeerConnection:${this.peerId}] Remote ${track.kind} track ${evt.type} (muted=${track.muted}, readyState=${track.readyState})`);
      if (track.kind === "video") {
        this._updateVideoHealth({ kind: track.kind, trackEvent: evt.type, trackMuted: track.muted, trackReadyState: track.readyState });
      }
    };
    track.addEventListener("mute", onChange);
    track.addEventListener("unmute", onChange);
    track.addEventListener("ended", onChange);
    this._remoteTrackCleanups.push(() => {
      track.removeEventListener("mute", onChange);
      track.removeEventListener("unmute", onChange);
      track.removeEventListener("ended", onChange);
    });
  }

  _unwatchRemoteTracks() {
    const cleanups = this._remoteTrackCleanups;
    this._remoteTrackCleanups = [];
    cleanups.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
  }

  // ==================== State Snapshot ====================

  getState() {
    return {
      peerId: this.peerId,
      robotId: this.robotId,
      state: this._state,
      iceState: this._iceState,
      connectionState: this._connectionState,
      autoConnect: this._autoConnect,
      wasConnected: this._wasConnected,
      cameras: this.cameras,
      microphones: this.microphones,
      speakers: this.speakers,
      isControlChannel: this.isControlChannel,
      retryCount: this._retryCount,
      microphoneEnabled: this._microphoneEnabled,
      remoteAudioEnabled: this._remoteAudioEnabled,
      userClosed: this._userClosed,
    };
  }

  // ==================== Internal: RTCPeerConnection Setup ====================

  _isStaleAttempt(seq, pc) {
    return this._destroyed || seq !== this._connectSeq || (pc !== null && this._pc !== pc);
  }

  /**
   * @returns {RTCPeerConnection} the newly created connection
   */
  _initPeerConnection() {
    // Close existing if any
    if (this._pc) {
      this._closePeerConnection();
    }

    logger.log(`[PeerConnection:${this.peerId}] Creating RTCPeerConnection`);
    const pc = new RTCPeerConnection(this.iceConfig);
    this._pc = pc;
    this._remoteTracks = new Set();
    this._pendingRemoteCandidates = [];
    this._remoteDescriptionSet = false;
    this._resetStats();
    this._startStatsTimer();

    // Create data channels
    this._dataChannels = {};
    for (const [channelName, params] of Object.entries(this.dataChannelsConfig)) {
      const dc = pc.createDataChannel(channelName, params);
      this._initDataChannel(dc);
      this._dataChannels[channelName] = dc;
    }

    // Event handlers — guarded so events of a replaced connection are ignored
    pc.onicecandidate = (e) => { if (this._pc === pc) this._onIceCandidate(e); };
    pc.addEventListener("track", (evt) => { if (this._pc === pc) this._onTrack(evt); });
    pc.addEventListener("connectionstatechange", (evt) => { if (this._pc === pc) this._onConnectionStateChange(evt); });
    pc.addEventListener("iceconnectionstatechange", (evt) => { if (this._pc === pc) this._onIceConnectionStateChange(evt); });
    pc.addEventListener("icegatheringstatechange", (evt) => {
      logger.debug(`[PeerConnection:${this.peerId}] ICE gathering: ${evt.target.iceGatheringState}`);
    });

    return pc;
  }

  /**
   * Add local tracks to the connection. Audio is only sent when the robot can
   * play it (speaker) and video only when explicitly enabled — mirrors the
   * legacy behaviour and keeps the offer minimal.
   */
  _addLocalTracks(localStream) {
    if (!localStream || !this._pc) return;
    const sendAudio = this.sendLocalAudio ?? this.hasSpeaker;
    localStream.getTracks().forEach((track) => {
      if (track.kind === "audio" && !sendAudio) return;
      if (track.kind === "video" && !this.sendLocalVideo) return;
      const sender = this._pc.addTrack(track, localStream);
      logger.log(`[PeerConnection:${this.peerId}] Added local track: ${track.kind}`);
      if (track.kind === "audio") {
        // The shared (silent) track keeps the audio m-line; the microphone is
        // swapped in on this sender when enabled for this peer.
        this._localAudioSender = sender || null;
        this._localAudioFallbackTrack = track;
        if (this._microphoneEnabled) {
          const mic = this._getMicrophoneTrack();
          if (mic) this._replaceLocalAudioTrack(mic);
        }
      }
    });
  }

  _waitForIceGathering(pc) {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const checkState = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", checkState);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", checkState);
    });
  }

  // ==================== Internal: Data Channel ====================

  _initDataChannel(dc) {
    dc.onopen = () => {
      logger.log(`[PeerConnection:${this.peerId}] Data channel '${dc.label}' opened`);
      this.eventBus.publish(
        getRobotEvent(this.peerId, ROBOT_EVENTS.CHANNEL_OPENED),
        { peerId: this.peerId, label: dc.label }
      );
    };
    dc.onclose = () => {
      logger.log(`[PeerConnection:${this.peerId}] Data channel '${dc.label}' closed`);
      this._publishChannelClosed(dc.label);
    };
    dc.onerror = (err) => {
      logger.error(`[PeerConnection:${this.peerId}] Data channel '${dc.label}' error:`, err);
      this.eventBus.publish(
        getRobotEvent(this.peerId, ROBOT_EVENTS.CHANNEL_ERROR),
        { peerId: this.peerId, label: dc.label, error: err?.error || err }
      );
    };
    dc.onmessage = (evt) => {
      this._onDataChannelMessage(dc.label, evt.data);
    };
  }

  _publishChannelClosed(label) {
    this.eventBus.publish(
      getRobotEvent(this.peerId, ROBOT_EVENTS.CHANNEL_CLOSED),
      { peerId: this.peerId, label }
    );
  }

  _onDataChannelMessage(channel, data) {
    const msg = { channel, data };

    // Publish channel message event
    this.eventBus.publish(
      getRobotEvent(this.peerId, ROBOT_EVENTS.CHANNEL_MESSAGE),
      msg
    );

    // Parse and publish topic messages
    if (this.publishTopicMessages && typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        if (parsed && parsed.topic !== undefined && parsed.value !== undefined) {
          const normalizedTopic = nomalizeRobotTopicName(parsed.topic);
          this.eventBus.publish(
            getRobotEvent(this.peerId, normalizedTopic),
            parsed.value
          );
        }
      } catch (e) {
        // Not JSON, ignore
      }
    }
  }

  // ==================== Internal: ICE ====================

  _onIceCandidate(e) {
    if (!e.candidate) return;
    if (this.trickleICE) {
      this.signalingBridge.sendIceCandidate(this.peerId, e.candidate);
    }
  }

  // ==================== Internal: Track ====================

  _onTrack(evt) {
    logger.log(`[PeerConnection:${this.peerId}] Remote track received:`, evt.track?.kind);
    this._remoteTracks.add(evt);
    this._watchRemoteTrack(this._pc, evt.track);
    this.eventBus.publish(
      getRobotEvent(this.peerId, ROBOT_EVENTS.MEDIA_TRACK_ADDED),
      { peerId: this.peerId, trackEvent: evt, tracks: Array.from(this._remoteTracks) }
    );
    this._updateVideoHealth({ kind: evt.track?.kind, trackEvent: "track" });
  }

  // ==================== Internal: Connection State ====================

  _onConnectionStateChange(evt) {
    const pc = evt.target;
    this._connectionState = pc.connectionState;
    logger.log(`[PeerConnection:${this.peerId}] Connection state: ${pc.connectionState}`);

    this._notifyConnectionState(pc.connectionState);

    if (pc.connectionState === "disconnected") {
      this._startDisconnectedWatchdog(pc);
    } else {
      this._clearDisconnectedWatchdog();
    }

    if (pc.connectionState === "connected") {
      this._wasConnected = true;
      this._retryCount = 0;
      this._clearReconnectTimer();
      this._clearAnswerTimer();
      this._clearIceTimer();
      this._setState(PEER_STATES.CONNECTED);
      // the robot sends audio by default after every new offer
      if (!this._remoteAudioEnabled) this._sendRemoteAudioConfig();
    } else if (pc.connectionState === "failed") {
      this._clearAnswerTimer();
      this._clearIceTimer();
      this._handleConnectionFailed();
    } else if (pc.connectionState === "closed") {
      // Only transition if not already closed/destroyed
      if (this._state !== PEER_STATES.CLOSED) {
        this._setState(PEER_STATES.CLOSED);
      }
    }

    this._publishTransportState();
    this._updateVideoHealth({ connectionState: pc.connectionState });
  }

  _startDisconnectedWatchdog(pc) {
    if (this._disconnectedTimer) return;
    this._disconnectedTimer = setTimeout(() => {
      this._disconnectedTimer = null;
      if (this._destroyed || this._pc !== pc) return;
      if (pc.connectionState !== "disconnected") return; // recovered in the meantime
      logger.warn(`[PeerConnection:${this.peerId}] Transport disconnected for ${DISCONNECTED_GRACE_MS}ms, reconnecting`);
      // Drop this connection first: if it recovered during the backoff its
      // 'connected' event would hit RECONNECTING (invalid transition) and
      // leave the peer stuck there with the retry timer cleared.
      this._closePeerConnection();
      this._handleConnectionFailed("transport disconnected");
    }, DISCONNECTED_GRACE_MS);
  }

  _clearDisconnectedWatchdog() {
    if (this._disconnectedTimer) {
      clearTimeout(this._disconnectedTimer);
      this._disconnectedTimer = null;
    }
  }

  _armAnswerTimer(seq, pc) {
    this._clearAnswerTimer();
    this._answerTimer = setTimeout(() => {
      this._answerTimer = null;
      if (this._isStaleAttempt(seq, pc) || this._remoteDescriptionSet) return;
      logger.warn(`[PeerConnection:${this.peerId}] No answer within ${ANSWER_TIMEOUT_MS}ms, retrying`);
      this._closePeerConnection();
      this._handleConnectionFailed("no answer");
    }, ANSWER_TIMEOUT_MS);
  }

  _clearAnswerTimer() {
    if (this._answerTimer) {
      clearTimeout(this._answerTimer);
      this._answerTimer = null;
    }
  }

  _armIceTimer(pc) {
    this._clearIceTimer();
    this._iceTimer = setTimeout(() => {
      this._iceTimer = null;
      if (this._destroyed || this._pc !== pc || pc.connectionState === "connected") return;
      logger.warn(`[PeerConnection:${this.peerId}] Not connected ${ICE_CONNECT_TIMEOUT_MS}ms after the answer (${pc.connectionState}), retrying`);
      this._closePeerConnection();
      this._handleConnectionFailed("ice timeout");
    }, ICE_CONNECT_TIMEOUT_MS);
  }

  _clearIceTimer() {
    if (this._iceTimer) {
      clearTimeout(this._iceTimer);
      this._iceTimer = null;
    }
  }

  /** Best-effort 'disconnect2robot' so the robot drops our peer immediately. */
  _sendDisconnect() {
    try {
      const result = this.signalingBridge.sendDisconnect?.(this.peerId);
      if (result && typeof result.catch === "function") {
        result.catch(() => { /* signaling not available — best effort */ });
      }
    } catch (e) {
      /* best effort */
    }
  }

  _onIceConnectionStateChange(evt) {
    const connection = evt.target;
    this._iceState = connection.iceConnectionState;
    logger.log(`[PeerConnection:${this.peerId}] ICE state: ${connection.iceConnectionState}`);
    this._publishTransportState();
  }

  /**
   * Raw RTCPeerConnection connectionState / iceConnectionState change (as
   * opposed to the state machine's CONNECTION_STATE event, which only fires on
   * machine transitions). Lets the UI show 'disconnected' / 'checking' while
   * the machine state is unchanged.
   */
  _publishTransportState() {
    this.eventBus.publish(
      getRobotEvent(this.peerId, WEBRTC_EVENTS.CONNECTION_STATE_CHANGED),
      {
        peerId: this.peerId,
        state: this._state,
        connectionState: this._connectionState,
        iceState: this._iceState,
        retryCount: this._retryCount,
      }
    );
  }

  /**
   * Schedule a reconnection attempt with exponential backoff, or give up
   * (FAILED) once maxRetries is reached.
   */
  _handleConnectionFailed(reason) {
    if (this._destroyed) return;
    // A retry is already scheduled — a second failure signal of the same
    // attempt (e.g. ICE failing after the offer was rejected) must not count.
    if (this._state === PEER_STATES.RECONNECTING && this._reconnectTimer) return;

    if (this._retryCount >= this.maxRetries) {
      logger.log(`[PeerConnection:${this.peerId}] Max retries (${this.maxRetries}) reached`, reason || "");
      this._closePeerConnection();
      this._setState(PEER_STATES.FAILED);
      return;
    }

    if (!this._setState(PEER_STATES.RECONNECTING)) return;
    this._retryCount++;

    const delay = Math.min(
      this.baseRetryDelay * Math.pow(2, this._retryCount - 1),
      RECONNECT_DEFAULTS.maxDelay
    );

    logger.log(`[PeerConnection:${this.peerId}] Retry ${this._retryCount}/${this.maxRetries} in ${delay}ms`);

    this._clearReconnectTimer();
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._destroyed || this._state !== PEER_STATES.RECONNECTING) return;
      this._closePeerConnection();
      // Emit event so ConnectionManager can provide fresh local stream
      this.eventBus.publish(
        getRobotEvent(this.peerId, PEER_INTERNAL_EVENTS.RECONNECT_NEEDED),
        { peerId: this.peerId, retryCount: this._retryCount }
      );
    }, delay);
  }

  // ==================== Internal: Cleanup ====================

  _closePeerConnection() {
    this._localAudioSender = null;
    this._localAudioFallbackTrack = null;
    // Close data channels — detach handlers first so no stale open/close/message
    // events leak out, then report channels that were open as closed.
    const channels = this._dataChannels;
    this._dataChannels = {};
    for (const dc of Object.values(channels)) {
      const wasOpen = dc.readyState === "open";
      dc.onopen = null;
      dc.onclose = null;
      dc.onmessage = null;
      dc.onerror = null;
      try { dc.close(); } catch (e) { /* ignore */ }
      if (wasOpen) this._publishChannelClosed(dc.label);
    }

    // Close peer connection
    if (this._pc) {
      const pc = this._pc;
      this._pc = null;
      try {
        if (pc.signalingState !== "closed") {
          pc.close();
        }
      } catch (e) { /* ignore */ }
    }

    this._clearDisconnectedWatchdog();
    this._clearAnswerTimer();
    this._clearIceTimer();
    this._unwatchRemoteTracks();
    this._remoteTracks = new Set();
    this._pendingRemoteCandidates = [];
    this._remoteDescriptionSet = false;
    this._iceState = null;
    this._connectionState = null;
    this._stopStatsTimer();
    this._resetStats();
  }

  _clearReconnectTimer() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  _notifyConnectionState(state) {
    try {
      const result = this.signalingBridge.sendEvent?.(this.peerId, "User2RobotConnectionStateChanged", {
        robot_id: this.peerId,
        state,
      });
      if (result && typeof result.catch === "function") {
        result.catch(() => { /* signaling not available — best effort */ });
      }
    } catch (e) {
      /* best effort */
    }
  }

  // ==================== Internal: Stats Collection ====================

  /**
   * Sample getStats() of the current connection, normalize it (see
   * peerStatsParser), update the media health and publish STATS_UPDATED:
   *   { peerId, sample, history, health, grade, state, connectionState, iceState }
   * `sample` is null-safe: video/audio sections are null until the first
   * inbound-rtp report exists.
   */
  _collectStats() {
    const pc = this._pc;
    if (!pc || this._statsUsers <= 0 || this._destroyed) return;

    pc.getStats(null).then((report) => {
      if (this._pc !== pc) return; // connection replaced while awaiting
      const previous = this.lastStatsSample;
      const sample = parseStatsReport(report, previous);
      this._statsSamples.push(sample);
      if (this._statsSamples.length > STATS_HISTORY_SIZE) {
        this._statsSamples.splice(0, this._statsSamples.length - STATS_HISTORY_SIZE);
      }

      const health = this._updateVideoHealth({ source: "stats" });
      const grade = gradeSample(sample);

      this.eventBus.publish(
        getRobotEvent(this.peerId, PEER_INTERNAL_EVENTS.STATS_UPDATED),
        {
          peerId: this.peerId,
          sample,
          health,
          grade,
          stalledSamples: STALL_SAMPLES,
          state: this._state,
          connectionState: this._connectionState,
          iceState: this._iceState,
          hasVideoTrack: !!this.remoteVideoTrack,
        }
      );
    }).catch((err) => {
      logger.debug(`[PeerConnection:${this.peerId}] getStats failed:`, err);
    });
  }
}
